import React from 'react';
import { motion } from 'framer-motion';
import {
  Sparkles, Loader2, Wand2, AlertCircle, Lightbulb, CheckCircle2, TrendingUp,
  Star, ArrowRight, GraduationCap, Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const scoreColor = (v) => {
  if (v >= 85) return '#D4FF00';
  if (v >= 70) return '#22C55E';
  if (v >= 55) return '#F59E0B';
  return '#EF4444';
};

const PLATFORM_META = {
  tiktok:   { label: 'TikTok',           accent: '#FF3D7F' },
  reels:    { label: 'Instagram Reels',  accent: '#A855F7' },
  shorts:   { label: 'YouTube Shorts',   accent: '#EF4444' },
  linkedin: { label: 'LinkedIn',         accent: '#3B82F6' },
};

const ScoreTile = ({ label, value, testid }) => {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const color = scoreColor(v);
  return (
    <div
      className="rounded-2xl border border-white/5 bg-white/[0.02] p-4"
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
          {label}
        </span>
        <span className="font-mono text-[11px] text-zinc-600 tabular-nums">/100</span>
      </div>
      <div className="flex items-end gap-2 mb-2">
        <span
          className="font-heading text-3xl font-black tabular-nums leading-none"
          style={{ color }}
        >
          {v}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{ width: `${v}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

const CoachPanel = ({ coach, loading, generating, error, onGenerate, lang }) => {
  const scores = coach?.scores || {};
  const overall = scores.viral ?? scores.overall ?? 0;
  const hasCoaching = (coach?.recommendations || []).length > 0
    || (coach?.strengths || []).length > 0
    || (coach?.hooks || []).length > 0
    || (coach?.platforms && Object.keys(coach.platforms).length > 0)
    || Boolean(coach?.learning)
    || Boolean(coach?.next_best_action);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
      data-testid="coach-panel"
    >
      {/* Header — overall score prominent */}
      <div
        className="rounded-2xl border border-[#D4FF00]/20 bg-gradient-to-br from-[#D4FF00]/[0.05] to-transparent p-6"
        data-testid="coach-overall"
      >
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#D4FF00]/10 border border-[#D4FF00]/30 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-[#D4FF00]" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-lg leading-tight">
                {lang === 'fr' ? 'AI Coach' : 'AI Coach'}
              </h3>
              <p className="text-xs text-zinc-500">
                {lang === 'fr'
                  ? 'Analyse, améliorations concrètes et points forts'
                  : 'Analysis, concrete improvements and strengths'}
              </p>
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
              AI Score
            </span>
            <span
              className="font-heading text-4xl font-black tabular-nums leading-none"
              style={{ color: scoreColor(overall) }}
              data-testid="coach-overall-score"
            >
              {overall}
            </span>
            <span className="font-mono text-sm text-zinc-500">/100</span>
          </div>
        </div>

        {/* 4 sub-scores */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="coach-score-grid">
          <ScoreTile label={lang === 'fr' ? 'Hook' : 'Hook'} value={scores.hook} testid="coach-score-hook" />
          <ScoreTile label={lang === 'fr' ? 'Rétention' : 'Retention'} value={scores.retention} testid="coach-score-retention" />
          <ScoreTile label={lang === 'fr' ? 'Émotion' : 'Emotion'} value={scores.emotion} testid="coach-score-emotion" />
          <ScoreTile label="CTA" value={scores.cta} testid="coach-score-cta" />
        </div>
      </div>

      {/* Loading initial fetch */}
      {loading && !coach ? (
        <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-8 text-center" data-testid="coach-loading">
          <Loader2 className="w-6 h-6 mx-auto text-[#D4FF00] animate-spin mb-3" />
          <p className="text-zinc-400 text-sm">
            {lang === 'fr' ? 'Chargement du coach…' : 'Loading coach…'}
          </p>
        </div>
      ) : null}

      {/* Empty coaching state — invite to generate */}
      {!loading && coach && !hasCoaching && !generating ? (
        <div className="rounded-2xl border border-dashed border-white/10 bg-[#0a0a0a]/50 p-10 text-center" data-testid="coach-empty-state">
          {error ? (
            <div className="flex flex-col items-center gap-3">
              <AlertCircle className="w-6 h-6 text-red-400" />
              <p className="text-sm text-red-400">{error}</p>
              <Button onClick={onGenerate} variant="outline" className="border-white/10 text-zinc-300">
                {lang === 'fr' ? 'Réessayer' : 'Retry'}
              </Button>
            </div>
          ) : (
            <>
              <Wand2 className="w-8 h-8 mx-auto text-[#D4FF00] mb-3" />
              <p className="text-zinc-300 text-sm mb-1 font-semibold">
                {lang === 'fr'
                  ? 'Obtiens des conseils concrets pour améliorer ce clip'
                  : 'Get concrete tips to improve this clip'}
              </p>
              <p className="text-zinc-500 text-xs mb-5 max-w-sm mx-auto">
                {lang === 'fr'
                  ? "GPT-5.2 analyse le transcript et te propose 3-5 améliorations actionnables + 4-6 points forts."
                  : 'GPT-5.2 analyses the transcript and returns 3-5 actionable improvements + 4-6 strengths.'}
              </p>
              <Button
                onClick={onGenerate}
                data-testid="coach-generate-btn"
                className="bg-[#D4FF00] text-black hover:bg-[#D4FF00]/90 font-semibold"
              >
                <Wand2 className="w-4 h-4 mr-2" />
                {lang === 'fr' ? 'Lancer le coach' : 'Launch coach'}
              </Button>
            </>
          )}
        </div>
      ) : null}

      {/* Generating spinner */}
      {generating ? (
        <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-10 text-center" data-testid="coach-generating">
          <Loader2 className="w-6 h-6 mx-auto text-[#D4FF00] animate-spin mb-3" />
          <p className="text-zinc-400 text-sm">
            {lang === 'fr'
              ? 'GPT-5.2 analyse ton clip et rédige les conseils…'
              : 'GPT-5.2 is analysing your clip and writing tips…'}
          </p>
        </div>
      ) : null}

      {/* Recommendations */}
      {hasCoaching && (coach?.recommendations || []).length > 0 ? (
        <section
          className="rounded-2xl border border-white/5 bg-white/[0.02] p-6"
          data-testid="coach-recommendations-section"
        >
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-[#F59E0B]" />
              <h3 className="font-heading font-bold text-base">
                {lang === 'fr' ? 'Comment améliorer ce clip' : 'How to improve this clip'}
              </h3>
            </div>
            <Button
              onClick={onGenerate}
              disabled={generating}
              variant="ghost"
              className="h-8 px-3 text-[11px] text-zinc-400 hover:text-white uppercase tracking-wider font-mono"
              data-testid="coach-regenerate-btn"
            >
              {generating ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1.5" />}
              {lang === 'fr' ? 'Regénérer' : 'Regenerate'}
            </Button>
          </div>
          <ol className="space-y-3">
            {coach.recommendations.map((rec, idx) => (
              <li
                key={idx}
                className="flex items-start gap-3 group"
                data-testid={`coach-rec-${idx}`}
              >
                <span className="flex-shrink-0 w-6 h-6 rounded-full border border-[#F59E0B]/40 bg-[#F59E0B]/5 text-[11px] font-mono font-bold text-[#F59E0B] flex items-center justify-center mt-0.5 tabular-nums">
                  {idx + 1}
                </span>
                <p className="text-sm text-zinc-200 leading-relaxed">{rec}</p>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      {/* Strengths */}
      {hasCoaching && (coach?.strengths || []).length > 0 ? (
        <section
          className="rounded-2xl border border-white/5 bg-white/[0.02] p-6"
          data-testid="coach-strengths-section"
        >
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-[#22C55E]" />
            <h3 className="font-heading font-bold text-base">
              {lang === 'fr' ? 'Pourquoi ce clip a du potentiel' : 'Why this clip has potential'}
            </h3>
          </div>
          <ul className="grid sm:grid-cols-2 gap-2.5">
            {coach.strengths.map((s, idx) => (
              <li
                key={idx}
                className="flex items-start gap-2.5"
                data-testid={`coach-strength-${idx}`}
              >
                <CheckCircle2 className="w-4 h-4 text-[#22C55E] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-zinc-200 leading-snug">{s}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* A/B Hook Generator */}
      {(coach?.hooks || []).length > 0 ? (
        <section
          className="rounded-2xl border border-white/5 bg-white/[0.02] p-6"
          data-testid="coach-hooks-section"
        >
          <div className="flex items-center gap-2 mb-1">
            <Zap className="w-4 h-4 text-[#D4FF00]" />
            <h3 className="font-heading font-bold text-base">
              {lang === 'fr' ? 'A/B Hook Generator' : 'A/B Hook Generator'}
            </h3>
          </div>
          <p className="text-xs text-zinc-500 mb-4">
            {lang === 'fr'
              ? "3 alternatives d'accroche à tester sur ce clip"
              : '3 alternative hooks to test on this clip'}
          </p>
          <div className="grid md:grid-cols-3 gap-3">
            {coach.hooks.map((h, idx) => {
              const stars = Math.max(1, Math.min(5, Number(h.stars) || 4));
              return (
                <div
                  key={idx}
                  className="rounded-xl border border-white/5 bg-[#0a0a0a] p-4 flex flex-col"
                  data-testid={`coach-hook-${idx}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
                      {lang === 'fr' ? `Hook ${String.fromCharCode(65 + idx)}` : `Hook ${String.fromCharCode(65 + idx)}`}
                    </span>
                    <div className="flex gap-0.5" data-testid={`coach-hook-stars-${idx}`}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          className={`w-3 h-3 ${n <= stars ? 'text-[#D4FF00] fill-[#D4FF00]' : 'text-zinc-700'}`}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-zinc-100 leading-snug mb-3 flex-1" data-testid={`coach-hook-text-${idx}`}>
                    &ldquo;{h.text}&rdquo;
                  </p>
                  <p className="text-xs text-zinc-500 leading-snug border-t border-white/5 pt-3 italic" data-testid={`coach-hook-why-${idx}`}>
                    {h.why}
                  </p>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* Predicted Platform Performance */}
      {coach?.platforms && Object.keys(coach.platforms).length > 0 ? (
        <section
          className="rounded-2xl border border-white/5 bg-white/[0.02] p-6"
          data-testid="coach-platforms-section"
        >
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-[#00E5FF]" />
            <h3 className="font-heading font-bold text-base">
              {lang === 'fr' ? 'Performance prédite par plateforme' : 'Predicted platform performance'}
            </h3>
          </div>
          <p className="text-xs text-zinc-500 mb-4">
            {lang === 'fr'
              ? 'Estimations IA basées sur les caractéristiques du clip'
              : 'AI estimates based on the clip characteristics'}
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {['tiktok', 'reels', 'shorts', 'linkedin'].map((key) => {
              const p = coach.platforms[key];
              if (!p) return null;
              const meta = PLATFORM_META[key];
              const score = Math.max(0, Math.min(100, Number(p.score) || 0));
              return (
                <div
                  key={key}
                  className="rounded-xl border border-white/5 bg-[#0a0a0a] p-4"
                  data-testid={`coach-platform-${key}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: meta.accent }}
                      />
                      <span className="text-sm font-semibold text-zinc-100">{meta.label}</span>
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span
                        className="font-heading text-2xl font-black tabular-nums leading-none"
                        style={{ color: scoreColor(score) }}
                        data-testid={`coach-platform-${key}-score`}
                      >
                        {score}
                      </span>
                      <span className="font-mono text-[10px] text-zinc-500">/100</span>
                    </div>
                  </div>
                  <div className="h-1 rounded-full bg-white/5 overflow-hidden mb-3">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${score}%`, backgroundColor: scoreColor(score) }}
                    />
                  </div>
                  <ul className="space-y-1.5 mb-3">
                    {(p.strengths || []).slice(0, 2).map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-zinc-300">
                        <CheckCircle2 className="w-3 h-3 text-[#22C55E] flex-shrink-0 mt-0.5" />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                  {p.weakness ? (
                    <div className="flex items-start gap-2 text-xs text-zinc-500 border-t border-white/5 pt-3">
                      <AlertCircle className="w-3 h-3 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                      <span>{p.weakness}</span>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* Learning Corner */}
      {coach?.learning ? (
        <section
          className="rounded-2xl border border-[#A855F7]/20 bg-gradient-to-br from-[#A855F7]/[0.05] to-transparent p-6"
          data-testid="coach-learning-section"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#A855F7]/10 border border-[#A855F7]/30 flex items-center justify-center flex-shrink-0">
              <GraduationCap className="w-4 h-4 text-[#A855F7]" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-bold text-base mb-1">
                {lang === 'fr' ? 'Learning Corner' : 'Learning Corner'}
              </h3>
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-3">
                {lang === 'fr'
                  ? 'Un principe qui s\u2019applique à CE clip'
                  : 'A principle that applies to THIS clip'}
              </p>
              <p className="text-sm text-zinc-200 leading-relaxed" data-testid="coach-learning-text">
                {coach.learning}
              </p>
            </div>
          </div>
        </section>
      ) : null}

      {/* Next Best Action */}
      {coach?.next_best_action ? (
        <section
          className="rounded-2xl border-2 border-[#D4FF00]/40 bg-gradient-to-br from-[#D4FF00]/[0.08] via-[#D4FF00]/[0.04] to-transparent p-6"
          data-testid="coach-nba-section"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-[#D4FF00]/15 border border-[#D4FF00]/40 flex items-center justify-center">
              <ArrowRight className="w-4 h-4 text-[#D4FF00]" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-base leading-tight">
                {lang === 'fr' ? 'Next Best Action' : 'Next Best Action'}
              </h3>
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
                {lang === 'fr'
                  ? "L'action la plus impactante à faire maintenant"
                  : 'The single most impactful action right now'}
              </p>
            </div>
          </div>
          <p
            className="text-base sm:text-lg text-zinc-50 font-semibold leading-snug pl-12"
            data-testid="coach-nba-text"
          >
            {coach.next_best_action}
          </p>
        </section>
      ) : null}
    </motion.div>
  );
};

export default CoachPanel;
